// Nameplates that would run into each other (2026-10-05): two tokens side by side in a fight, at the zoom that fits
// a 30-square map on a 1920-pixel screen, drew "Shelby" and "Goblin 4" as one smear, "Shelbyblin 4". A plate keeps
// its whole name unless it would overlap a neighbour's on the same row; then the two share the room between their
// centres — a short name keeps its length and leaves the rest to the long one — and the cut name ends in "…".
// Pure, in screen pixels, so it is tested without a renderer.

export interface Plate { id: string; cx: number; top: number; bottom: number; width: number }

/** The widest each plate may be drawn, for the plates that must be cut; a plate absent from the map keeps its width. */
export function plateLimits(plates: readonly Plate[], pad = 4): Map<string, number> {
  const out = new Map<string, number>();
  const limit = (id: string, w: number) => { const cur = out.get(id); out.set(id, cur === undefined ? w : Math.min(cur, w)); };
  const sorted = [...plates].sort((a, b) => a.cx - b.cx);
  const widest = sorted.reduce((m, p) => Math.max(m, p.width), 0);
  for (let i = 0; i < sorted.length; i += 1) {
    const a = sorted[i]!;
    for (let j = i + 1; j < sorted.length; j += 1) {
      const b = sorted[j]!;
      const d = b.cx - a.cx;
      if (d >= (a.width + widest) / 2 + pad) break; // sorted by centre: nothing further right can reach a
      if (a.top >= b.bottom || b.top >= a.bottom) continue; // different rows
      if (d >= (a.width + b.width) / 2 + pad) continue; // they clear each other
      const room = Math.max(0, d - pad); // each may be this wide and still clear the other, centred
      if (a.width <= room) limit(b.id, 2 * room - a.width);
      else if (b.width <= room) limit(a.id, 2 * room - b.width);
      else { limit(a.id, room); limit(b.id, room); }
    }
  }
  return out;
}

/** The longest start of `name` that, with "…", fits `max`; the whole name when it fits; never less than one letter. */
export function fitName(name: string, max: number, measure: (s: string) => number): string {
  if (measure(name) <= max) return name;
  const chars = Array.from(name);
  let lo = 1; let hi = chars.length - 1; let best = 1;
  while (lo <= hi) {
    const k = (lo + hi) >> 1;
    if (measure(`${chars.slice(0, k).join('').trimEnd()}…`) <= max) { best = k; lo = k + 1; } else hi = k - 1;
  }
  return `${chars.slice(0, best).join('').trimEnd()}…`;
}
